import applyIsoDuration from '@bridebook/toolbox/src/applyIsoDuration';
import { periodMonthsBefore } from './blueprint/group-timings';
import type { BlueprintGroup, ResolvedTask } from './blueprint/types';

export type ChecklistTaskState = ResolvedTask;

// How many of the nearest groups list their open task names in the summary. Enough for
// a timeline reply; every other group is one row of counts.
export const DETAILED_GROUPS = 8;

// One checklist group as a row, as the checklist tool reads it. Its intro and subtasks
// stay in the browser's store, where the card looks them up by group id.
export type UpcomingGroup = {
  groupId: string;
  groupName: string;
  supplierType: string | null;
  period: string;
  /**
   * The calendar month the group starts in, as "YYYY-MM", where the checklist page lists it.
   * Null without a wedding date, and for an "anytime" group. Missing from a summary that an
   * older client sent.
   */
  month?: string | null;
  isOverdue: boolean;
  doneCount: number;
  totalCount: number;
  // Open task names, in checklist order.
  openTasks: string[];
  // A custom main task the couple added, which heads its own group.
  custom?: boolean;
};

// Whole-month index, so periods compare at the same level as the "MMM yyyy" label.
// Uses local time, as the label does.
const yearMonthIndex = (d: Date): number => d.getFullYear() * 12 + d.getMonth();

// Sorting key, furthest from the wedding first: '-P' ("anytime") has no month, so it sorts last.
const periodSortKey = (period: string): number => -(periodMonthsBefore(period) ?? -Infinity);

const yearMonth = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const selectUpcomingGroups = (
  allTasks: readonly ChecklistTaskState[],
  options: {
    count: number;
    groups: Record<string, BlueprintGroup>;
    weddingDate?: Date | null;
    today?: Date;
    // A projected checklist was never assigned, so nothing in it can be overdue.
    projected?: boolean;
  },
): UpcomingGroup[] => {
  const { count, groups, weddingDate = null, today = new Date(), projected = false } = options;
  const currentMonth = yearMonthIndex(today);
  const incomplete = allTasks.filter((t) => !t.done);

  const byGroup = new Map<string, ChecklistTaskState[]>();
  for (const t of incomplete) {
    const list = byGroup.get(t.group) ?? [];
    list.push(t);
    byGroup.set(t.group, list);
  }

  // Full task set per group (done + outstanding) for accurate counts; the incomplete map
  // above drives selection and the open names.
  const allByGroup = new Map<string, ChecklistTaskState[]>();
  for (const t of allTasks) {
    const list = allByGroup.get(t.group) ?? [];
    list.push(t);
    allByGroup.set(t.group, list);
  }

  const scored: { group: UpcomingGroup; minOrder: number }[] = [];
  for (const [groupId, tasks] of byGroup.entries()) {
    const meta = groups[groupId];
    const groupTasks = [...(allByGroup.get(groupId) ?? [])].sort((a, b) => a.order - b.order);
    // A custom main task heads its own group, which the template does not know. The
    // checklist page lists it, so it is a group here too.
    const head = groupTasks.find((t) => t.id === groupId);
    const custom = !meta && Boolean(head?.custom);
    if (!meta && !custom) {
      continue;
    }
    const sortedTasks = [...tasks].sort((a, b) => a.order - b.order);
    const earliest = [...tasks].sort(
      (a, b) => periodSortKey(a.period) - periodSortKey(b.period),
    )[0];
    const monthsBeforeWedding = periodMonthsBefore(earliest.period);
    const start =
      weddingDate && monthsBeforeWedding != null
        ? applyIsoDuration(weddingDate, earliest.period)
        : null;

    // Overdue only once the target month has fully passed; a task due this month is
    // upcoming. Uses the same date as the "MMM yyyy" label, so the two always agree.
    const isOverdue = !projected && start ? yearMonthIndex(start) < currentMonth : false;

    scored.push({
      group: {
        groupId,
        groupName: meta?.name ?? head?.name ?? groupId,
        supplierType: meta?.supplierType ?? null,
        period: earliest.period,
        month: start ? yearMonth(start) : null,
        isOverdue,
        doneCount: groupTasks.filter((t) => t.done).length,
        totalCount: groupTasks.length,
        openTasks: sortedTasks.map((t) => t.name),
        ...(custom ? { custom: true } : {}),
      },
      minOrder: sortedTasks[0].order,
    });
  }

  // Checklist order, earliest open task first, as the checklist page lists the groups.
  scored.sort((a, b) => a.minOrder - b.minOrder);
  return scored.map((s) => s.group).slice(0, count);
};
